import { Component, OnInit, Input, inject, runInInjectionContext, Injector } from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  IonHeader,
  IonFooter,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  ModalController,
  AlertController,
  IonText,
  IonBadge,
} from '@ionic/angular/standalone';

import { onValue, DataSnapshot, get } from 'firebase/database';

import { DataService } from '../../services/data.service';
import { ROLES } from 'src/app/services/constants';
import { TeamActionsService } from '../../services/action-services/team-actions.service';
import {
  AdminRoleDefinition,
  ADMIN_ROLE_DEFINITIONS,
  getAdminRoleByDisplayName,
  isElevatedRole,
  normalizeRoleId,
} from '../../services/role-constants';

@Component({
  selector: 'app-add-team-member',
  templateUrl: './add-team-member.page.html',
  styleUrls: ['./add-team-member.page.scss'],
  standalone: true,
  imports: [
    FormsModule,
    IonHeader,
    IonFooter,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonText,
    IonInput,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonBadge,
  ],
})
export class AddTeamMemberPage implements OnInit {
  public modalCtrl: ModalController = inject(ModalController);
  public alertCtrl: AlertController = inject(AlertController);
  public dataService: DataService = inject(DataService);
  private teamActions: TeamActionsService = inject(TeamActionsService);
  private injector = inject(Injector);

  @Input() pageType: any;
  @Input() userId: any;

  public userRole: any;
  public description: any = '';
  public changeReason: string = '';

  public lookupQuery: string = '';
  public matchedUser: {
    uid: string;
    email: string | null;
    displayName: string | null;
    profileName?: string | null;
    photoURL: string | null;
    phoneNumber: string | null;
    disabled: boolean;
  } | null = null;
  public isLookingUp: boolean = false;

  public alreadyTeamMember: boolean = false;
  public existingTeamRole: string | null = null;

  get matchedUserSummary(): string {
    if (!this.matchedUser) return '';
    const email = this.matchedUser.email || '—';
    const name = this.matchedUser.profileName || this.matchedUser.displayName || '—';
    const disabled = this.matchedUser.disabled ? 'Yes' : 'No';
    const already = this.alreadyTeamMember ? 'Yes' : 'No';
    const role = this.existingTeamRole || '—';
    return `UID: ${this.matchedUser.uid}\nEmail: ${email}\nName: ${name}\nDisabled: ${disabled}\nAlready Team Member: ${already}\nExisting Role: ${role}`;
  }

  roles: any = ROLES;

  ngOnInit() {
    runInInjectionContext(this.injector, () => {
      if (this.userId) {
        this.lookupQuery = this.userId;
        try {
          const accessRef = this.dataService.getAdminPortalAccess(this.userId);
          onValue(accessRef as any, (aboutRes: DataSnapshot) => {
            if (aboutRes.exists()) {
              this.userRole = aboutRes.val().role;
              this.description = aboutRes.val().description;
              this.alreadyTeamMember = aboutRes.val()?.enabled === true;
              this.existingTeamRole = aboutRes.val()?.role || null;
            }
          });
        } catch {
          // Best-effort: avoid crashing when the RTDB ref isn't available (e.g., unit tests or misconfigured DI).
        }

        // Best-effort lookup so the modal shows a confirmation preview.
        // If it fails, user can still proceed in Edit mode.
        void this.lookupUser();
      }
    });
  }

  async lookupUser() {
    const query = (this.lookupQuery || '').toString().trim();
    if (!query) {
      const alert = await this.alertCtrl.create({
        message: 'Please enter a user email or UID to search.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    this.isLookingUp = true;
    this.matchedUser = null;
    this.alreadyTeamMember = false;
    this.existingTeamRole = null;

    try {
      const result = await this.teamActions.lookupUser(query);
      this.matchedUser = result;
      this.userId = result?.uid;

      if (this.userId) {
        await this.checkExistingTeamAccess(this.userId);
        if (this.pageType === 'Add' && this.alreadyTeamMember) {
          const alert = await this.alertCtrl.create({
            message: 'This user is already added as a team member.',
            buttons: ['OK'],
          });
          await alert.present();
        }
      }
    } catch (error: any) {
      const alert = await this.alertCtrl.create({
        message: error?.message ? error.message : 'No user found for that email/uid.',
        buttons: ['OK'],
      });
      await alert.present();
    } finally {
      this.isLookingUp = false;
    }
  }

  private async checkExistingTeamAccess(uid: string) {
    try {
      const accessRef = this.dataService.getAdminPortalAccess(uid);
      const snap = await get(accessRef as any);
      if (snap.exists()) {
        this.alreadyTeamMember = snap.val()?.enabled === true;
        this.existingTeamRole = snap.val()?.role || null;
      } else {
        this.alreadyTeamMember = false;
        this.existingTeamRole = null;
      }
    } catch {
      // Best-effort: don't block lookup if RTDB isn't reachable.
      this.alreadyTeamMember = false;
      this.existingTeamRole = null;
    }
  }

  async options() {
    if (!this.userRole) {
      const alert = await this.alertCtrl.create({ message: 'Please select a role.', buttons: ['OK'] });
      await alert.present();
      return;
    }

    // In Add mode, force a lookup-confirmed UID to reduce risk of granting access to the wrong user.
    if (this.pageType === 'Add' && !this.matchedUser?.uid) {
      const alert = await this.alertCtrl.create({
        message: 'Please search and confirm the user by email/uid before granting access.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    if (this.pageType === 'Add' && this.alreadyTeamMember) {
      const alert = await this.alertCtrl.create({
        message: 'This user is already added as a team member.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    if (!this.userId) {
      const alert = await this.alertCtrl.create({ message: 'Please provide a user UID.', buttons: ['OK'] });
      await alert.present();
      return;
    }

    if (!this.changeReason.trim() || this.changeReason.trim().length < 10) {
      const alert = await this.alertCtrl.create({
        message: 'Please provide a clear reason for this team-member claims change.',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    try {
      await this.teamActions.setTeamMemberClaims({
        uid: this.userId,
        enabled: true,
        role: this.userRole,
        description: this.description,
        reason: this.changeReason.trim(),
        requireNotExists: this.pageType === 'Add',
      });

      const alert = await this.alertCtrl.create({
        message: this.pageType === 'Add' ? 'User added successfully...' : 'User updated successfully...',
        buttons: ['OK'],
      });
      await alert.present();
      this.dismiss();
    } catch (error: any) {
      const alert = await this.alertCtrl.create({
        message: error?.message ? error.message : 'Failed to update team member.',
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // ROLE DISPLAY HELPERS (Phase 9: Access Control UI)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Get role definition by display name for badge rendering */
  getRoleDefinition(roleName: string | undefined): AdminRoleDefinition | undefined {
    if (!roleName) return undefined;
    return getAdminRoleByDisplayName(roleName);
  }

  /** Get Ionic color for role badge */
  getRoleColor(roleName: string | undefined): string {
    const def = this.getRoleDefinition(roleName);
    return def?.color || 'medium';
  }

  /** Get Ionic icon for role badge */
  getRoleIcon(roleName: string | undefined): string {
    const def = this.getRoleDefinition(roleName);
    return def?.icon || 'person';
  }

  /** Check if role requires dual-control (elevated) */
  isRoleElevated(roleName: string | undefined): boolean {
    if (!roleName) return false;
    const normalizedId = normalizeRoleId(roleName);
    if (!normalizedId) return false;
    return isElevatedRole(normalizedId);
  }

  /** Get all role definitions for template rendering */
  get allRoleDefinitions(): AdminRoleDefinition[] {
    return ADMIN_ROLE_DEFINITIONS;
  }

  dismiss() {
    this.modalCtrl.dismiss({ dismissed: true });
  }
}
